import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Banner from "../../../Components/Common/Banner";
import banner from "../../../Assets/images/BannerImages/application_development.png";
import GetInTouchUs from "../../../Components/Common/GetInTouchUs";

/* ══════════════════════════════════════════
   HOOKS
══════════════════════════════════════════ */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ══════════════════════════════════════════
   SMALL REUSABLES
══════════════════════════════════════════ */
function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(28px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function Skeleton({ className }) {
  return (
    <div
      className={`animate-pulse bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:200%_100%] rounded-xl ${className}`}
    />
  );
}

/* ══════════════════════════════════════════
   HERO SKELETON
══════════════════════════════════════════ */
function HeroSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 animate-pulse">
      <div className="h-[70vh] bg-slate-200" />
      <div className="max-w-5xl mx-auto px-6 py-16 space-y-6">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-4/5" />
        <Skeleton className="h-5 w-3/5" />
      </div>
    </div>
  );
}
function ProductCard({ item, index }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className="group relative bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 0.6s ease ${index * 120}ms, transform 0.6s ease ${index * 120}ms, box-shadow 0.3s, translate 0.3s`,
      }}
    >
      {/* Card image */}
      <div className="relative h-52 overflow-hidden bg-gradient-to-br from-blue-50 to-slate-100">
        {item.image ? (
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => (e.target.style.display = "none")}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl text-blue-200 select-none">
            🔷
          </div>
        )}
        {/* Blue overlay on hover */}
        <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/10 transition-colors duration-300" />
        {/* Index chip */}
        <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-xs font-bold text-blue-600 shadow-sm border border-blue-100">
          {String(index + 1).padStart(2, "0")}
        </div>
      </div>

      {/* Card body */}
      <div className="p-6">
        <h3 className="font-bold text-slate-800 text-lg mb-2 group-hover:text-blue-600 transition-colors duration-200 leading-snug">
          {item.title}
        </h3>
        <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">
          {item.description}
        </p>
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-blue-500 to-blue-300 group-hover:w-full transition-all duration-500 rounded-b-2xl" />
    </div>
  );
}

function Breadcrumb({ heading }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
      <a href="/" className="hover:text-blue-600 transition-colors font-medium">
        Home
      </a>
      <span className="text-slate-300">/</span>
      <a
        href="/services"
        className="hover:text-blue-600 transition-colors font-medium"
      >
        Services
      </a>
      <span className="text-slate-300">/</span>
      <span className="text-slate-600 font-semibold truncate max-w-[200px]">
        {heading}
      </span>
    </nav>
  );
}

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function ServiceDetails() {
  const { id } = useParams();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scrollY, setScrollY] = useState(0);

  /* Parallax scroll */
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Fetch service by id */
  useEffect(() => {
    if (!id) return;

    const fetchService = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/services/get-service/${id}`,
        );
        if (res.status === 404) {
          throw new Error("Service not found");
        }
        if (!res.ok) {
          throw new Error("Failed to load service");
        }

        const data = await res.json();

        setService(data.service);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  /* ── Loading ── */
  if (loading) return <HeroSkeleton />;

  /* ── Error ── */
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-6 px-6 text-center">
        <div className="w-20 h-20 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-4xl">
          🔍
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{error}</h2>
          <p className="text-slate-400 text-sm">
            The service you're looking for may have been moved or removed.
          </p>
        </div>
        <a
          href="/services"
          className="px-6 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition shadow-sm"
        >
          ← Back to Services
        </a>
      </div>
    );
  }

  if (!service) return null;

  const paragraphs = service.introSection?.paragraphs?.filter(Boolean) ?? [];
  const productCards = service.moreProductInfo ?? [];
  const subtitle = service.introSection?.subtitle;

  return (
    <div className="bg-[#fafafa] min-h-screen">
      <Banner
        pageName={service.heading}
        breadcrummbs={service.heading}
        bgimage={banner}
      />

      <div className=" px-4 lg:px-10 py-14">
        {/* INTRO */}

        <section className="mb-8">
          <FadeIn>
            <div className="bg-white rounded-3xl p-8 lg:p-14">
              <div className="text-center mb-10">
                <div className="inline-block px-4 py-2 rounded-full bg-blue-50 text-blue-600 font-semibold text-sm mb-4">
                  ABOUT OUR SERVICE
                </div>

                <h1 className="lg:text-4xl text-2xl font-bold text-gray-800">
                  {service.introSection?.subtitle}
                </h1>
              </div>

              <div className="space-y-5 text-gray-600 leading-8">
                {service.introSection?.paragraphs?.map((paragraph, index) => (
                  <div
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: paragraph,
                    }}
                  />
                ))}
              </div>
            </div>
          </FadeIn>
        </section>

        {/* FEATURE IMAGE */}

        {service.mainImage && (
          <section className="mb-8">
            <FadeIn>
              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img
                  src={service.mainImage}
                  alt={service.heading}
                  className="w-full h-[640px] object-fill "
                />
              </div>
            </FadeIn>
          </section>
        )}

        {/* SERVICES FEATURES */}

        {productCards?.length > 0 && (
        <section className="py-12">
  <div className="max-w-7xl mx-auto px-4">

    <h2 className="lg:text-4xl text-2xl font-bold text-gray-800 text-center mb-10">
      What We Provide
    </h2>

    <div className="space-y-8">
      {productCards.map((item, index) => (
        <FadeIn key={index} delay={index * 100}>
          <div className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 flex flex-col lg:flex-row min-h-[400px]">

            {/* Image */}
            <div className="lg:w-[40%] w-full h-[250px] lg:h-[400px] flex-shrink-0 overflow-hidden">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>

            {/* Content */}
            <div className="lg:w-[60%] p-8 flex flex-col justify-center">
              <h3 className="font-bold text-2xl text-gray-800 mb-4">
                {item.title}
              </h3>

              <div
                className="text-gray-600 text-base leading-7"
                dangerouslySetInnerHTML={{
                  __html: item.description,
                }}
              />
            </div>

          </div>
        </FadeIn>
      ))}
    </div>

  </div>
</section>
        )}
      </div>

      <div className="px-12 py-4">
        <GetInTouchUs />
      </div>

      
    </div>
  );
}
